import { captureException } from "@sentry/react";
import * as React from "react";
import { ErrorScreen } from "@medbillai/app/components/ErrorScreen";
import { analyticsService } from "@medbillai/app/lib/analytics";
import { ApolloGraphQLError, ApolloNetworkError } from "@medbillai/app/lib/apollo";
import { classifyErrorSource } from "@medbillai/app/lib/sentry/classifyErrorSource";
import { TamaguiProvider } from "@medbillai/app/provider/tamagui/TamaguiProvider";
import { Body1, ExternalLink } from "@medbillai/ui";

// Special screen for custom error handling by Next.js. Note that this is not
// rendered as its own page/route, but rather a component that is rendered in
// place of the current page.

const networkError = ["Could not connect to Granted. Please check your internet connection.", "If your internet is OK, then it's probably a problem on our side. Please try again in a few minutes."];
const bugError = ["We're having some technical difficulties. You can try refreshing the page."];
const generalError = ["Something went wrong. You can try refreshing the page."];
type ErrorBoundaryProps = {
  children: React.ReactNode;
  name: string;
};
type ErrorBoundaryState = {
  hasError: boolean;
  error?: Error;
  info?: React.ErrorInfo;
};
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false
    };
  }
  static getDerivedStateFromError(error: Error) {
    return {
      hasError: true,
      error
    };
  }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.log("[Error boundary] Error", error);
    console.log("[Error boundary] Info", info);
    this.setState({
      hasError: true,
      error,
      info
    });
    captureException(error);
  }
  render() {
    if (this.state.hasError) {
      return <TamaguiProvider data-sentry-element="TamaguiProvider" data-sentry-component="ErrorBoundary" data-sentry-source-file="ErrorBoundary.tsx">
          <CrashScreen error={this.state.error} info={this.state.info} errorBoundaryName={this.props.name} data-sentry-element="CrashScreen" data-sentry-source-file="ErrorBoundary.tsx" />
        </TamaguiProvider>;
    }
    return this.props.children;
  }
}
const CrashScreen = ({
  error,
  info,
  errorBoundaryName
}: {
  error?: Error;
  info?: React.ErrorInfo;
  errorBoundaryName: string;
}) => {
  const messages: string[] = [];
  if (error) {
    console.log("[Error screen] Error", error);
    if (error instanceof ApolloNetworkError) {
      messages.push(...networkError);
      analyticsService.trackEvent("ErrorScreenDisplayed", {
        screen: "NETWORK",
        errorType: "ApolloNetworkError",
        message: error.message
      });
      console.log("[Error screen] ApolloNetworkError", error.message);
    } else if (error instanceof ApolloGraphQLError) {
      messages.push(...bugError);
      analyticsService.trackEvent("ErrorScreenDisplayed", {
        screen: "BUG",
        errorType: "ApolloGraphQLError",
        message: error.message
      });
      // Error has already been sent to Sentry in the error link.
      console.log("[Error screen] ApolloGraphQLError", error.message);
    } else {
      // Send non-GQL errors to Sentry.
      captureException(error, {
        extra: {
          info
        },
        tags: {
          fromErrorBoundary: errorBoundaryName
        }
      });
      console.log("[Error screen] Non-GQL error", error.message);
    }
  } else {
    console.log("[Error screen] Showing general error screen");
  }
  if (messages.length === 0) {
    messages.push(...generalError);
  }
  return <ErrorScreen title="We experienced an error" withoutShadowBanner report={{
    screen: "web-crash",
    source: classifyErrorSource(error, "error-boundary"),
    error
  }} action={{
    label: "Refresh",
    onPress: () => {
      if (window) {
        void window.location.reload();
      }
    }
  }} data-sentry-element="ErrorScreen" data-sentry-component="CrashScreen" data-sentry-source-file="ErrorBoundary.tsx">
      {messages.map((message, index) => <Body1 key={index} color="$textSubtle" textAlign="center">
          {message}
        </Body1>)}
      <Body1 color="$textSubtle" textAlign="center" data-sentry-element="Body1" data-sentry-source-file="ErrorBoundary.tsx">
        Please reach out to{" "}
        <ExternalLink href="mailto:support@grantedhealth.com" title="support@grantedhealth.com" data-sentry-element="ExternalLink" data-sentry-source-file="ErrorBoundary.tsx" />{" "}
        if this issue persists.
      </Body1>
    </ErrorScreen>;
};